'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { useTranslation } from '@/hooks/useTranslation';
import { CustomReactMarkdown } from '@/components/common/CustomReactMarkdown';
import {
  HiOutlineArrowLeft,
  HiOutlineChevronRight,
  HiOutlineCpuChip,
  HiOutlineCube,
  HiOutlineLink,
  HiOutlineShare,
  HiOutlineTag,
  HiOutlineXMark,
  HiOutlineClipboardDocument,
  HiOutlineCheck,
  HiOutlineUser,
} from 'react-icons/hi2';
import { FaLinkedinIn, FaYoutube } from 'react-icons/fa';
import { BiMoviePlay, BiSolidConversation } from 'react-icons/bi';
import { AiOutlineGlobal } from 'react-icons/ai';
import Image from 'next/image';
import { MarketplacePackage } from '@/utils/marketplace';

interface PageContentProps {
  locale: string;
  initialData: MarketplacePackage | null;
}

export default function PageContent({ locale, initialData }: PageContentProps) {
  const { t } = useTranslation(locale);
  const [packageData] = useState<MarketplacePackage | null>(initialData);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const loading = false;
  const error = initialData === null ? 'Failed to load package data, retry later' : null;

  const getShareableUrl = () => {
    if (!packageData?.name) return '';
    return `https://louisebot.com/en-US/marketplace/${encodeURIComponent(packageData.name)}`;
  };

  const copyShareableLink = async () => {
    const url = getShareableUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyFailed(false);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Show the URL for manual selection when clipboard API fails
      setCopyFailed(true);
    }
  };

  const getBotTypeLabel = (type: string) => {
    return t(`Marketplace/detail/bots/types/${type}`) || type;
  };

  const getBotTypeIcon = (type: string) => {
    const icons: { [key: string]: JSX.Element } = {
      MCBOT_01: <BiSolidConversation className="tw-w-8 tw-h-8" />,
      KITTY_02: <HiOutlineCpuChip className="tw-w-8 tw-h-8" />,
      VIDEO_SCENARIO: <BiMoviePlay className="tw-w-8 tw-h-8" />,
      KITTY_03: <BiSolidConversation className="tw-w-8 tw-h-8" />,
      KITTY_04: <AiOutlineGlobal className="tw-w-8 tw-h-8" />,
    };
    return icons[type] || <HiOutlineCpuChip className="tw-w-8 tw-h-8" />;
  };

  const getBotTypeColor = (type: string) => {
    const colors: { [key: string]: { gradient: string; badge: string; text: string; border: string } } = {
      MCBOT_01: {
        gradient: 'tw-from-blue-50 tw-to-blue-100',
        badge: 'tw-bg-blue-100 tw-text-blue-800',
        text: 'tw-text-blue-600',
        border: 'tw-border-blue-200',
      },
      KITTY_02: {
        gradient: 'tw-from-purple-50 tw-to-purple-100',
        badge: 'tw-bg-purple-100 tw-text-purple-800',
        text: 'tw-text-purple-600',
        border: 'tw-border-purple-200',
      },
      VIDEO_SCENARIO: {
        gradient: 'tw-from-pink-50 tw-to-pink-100',
        badge: 'tw-bg-pink-100 tw-text-pink-800',
        text: 'tw-text-pink-600',
        border: 'tw-border-pink-200',
      },
      KITTY_03: {
        gradient: 'tw-from-green-50 tw-to-green-100',
        badge: 'tw-bg-green-100 tw-text-green-800',
        text: 'tw-text-green-600',
        border: 'tw-border-green-200',
      },
      KITTY_04: {
        gradient: 'tw-from-orange-50 tw-to-orange-100',
        badge: 'tw-bg-orange-100 tw-text-orange-800',
        text: 'tw-text-orange-600',
        border: 'tw-border-orange-200',
      },
    };
    return (
      colors[type] || {
        gradient: 'tw-from-gray-50 tw-to-gray-100',
        badge: 'tw-bg-gray-100 tw-text-gray-800',
        text: 'tw-text-gray-600',
        border: 'tw-border-gray-200',
      }
    );
  };

  if (loading) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 tw-py-12 tw-px-4">
        <div className="tw-max-w-7xl tw-mx-auto">
          <div className="tw-flex tw-items-center tw-justify-center tw-h-96">
            <div className="tw-text-center">
              <div className="tw-inline-block tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-600"></div>
              <p className="tw-mt-4 tw-text-gray-600 tw-text-lg">{t('Marketplace/detail/loading')}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 tw-py-12 tw-px-4">
        <div className="tw-max-w-7xl tw-mx-auto">
          <div className="tw-bg-white tw-rounded-2xl tw-shadow-lg tw-p-8 tw-border tw-border-red-200">
            <div className="tw-text-center">
              <div className="tw-mx-auto tw-flex tw-items-center tw-justify-center tw-h-12 tw-w-12 tw-rounded-full tw-bg-red-100">
                <HiOutlineXMark className="tw-h-6 tw-w-6 tw-text-red-600" />
              </div>
              <h3 className="tw-mt-4 tw-text-lg tw-font-medium tw-text-gray-900">
                {t('Marketplace/detail/errorTitle')}
              </h3>
              <p className="tw-mt-2 tw-text-red-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!packageData) return null;

  const hasResources =
    packageData.data.promotional_video || packageData.data.youtube_link || packageData.data.linkedin_link || false;

  return (
    <div className="tw-min-h-screen tw-bg-gradient-to-br tw-from-gray-50 tw-to-gray-100 tw-py-12 tw-px-4">
      <div className="tw-max-w-7xl tw-mx-auto">
        {/* Back Button */}
        <Link
          href={`/${locale}/marketplace#package-${packageData.package_id}`}
          className="tw-inline-flex tw-items-center tw-mb-6 tw-text-gray-600 hover:tw-text-blue-600 tw-transition-colors tw-duration-200 tw-group"
        >
          <HiOutlineArrowLeft className="tw-w-5 tw-h-5 tw-mr-2 tw-transform group-hover:tw--translate-x-1 tw-transition-transform" />
          <span className="tw-font-medium">{t('Marketplace/detail/backToMarketplace')}</span>
        </Link>

        <div className={`${hasResources ? 'tw-grid' : ''} tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-8`}>
          {/* Left Column - Main Content */}
          <div className="lg:tw-col-span-2 tw-space-y-8">
            {/* Header Section */}
            <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden">
              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-0">
                {/* Image Section */}
                <div className="md:tw-col-span-2 tw-bg-gradient-to-br tw-from-blue-50 tw-to-indigo-50 tw-p-8 tw-flex tw-items-start tw-justify-center">
                  {packageData?.data?.image ? (
                    <Image
                      priority
                      quality={100}
                      crossOrigin="anonymous"
                      src={packageData.data.image}
                      width={1200}
                      height={630}
                      title={packageData.name}
                      alt={packageData.name}
                      className="tw-rounded-md"
                    />
                  ) : (
                    <div className="tw-w-full tw-h-64 tw-bg-gradient-to-br tw-from-blue-100 tw-to-indigo-100 tw-rounded-xl tw-flex tw-items-center tw-justify-center">
                      <HiOutlineCube className="tw-w-24 tw-h-24 tw-text-blue-300" />
                    </div>
                  )}
                </div>

                {/* Package Info Section */}
                <div className="md:tw-col-span-3 tw-p-8">
                  <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                    <div>
                      <h1 className="tw-text-3xl tw-font-bold tw-text-gray-900 tw-mb-2">{packageData.name}</h1>
                    </div>
                  </div>

                  <CustomReactMarkdown
                    content={packageData.data.description || ''}
                    className="tw-text-gray-700 tw-text-base tw-mb-6 tw-leading-relaxed"
                  />

                  {/* Stats Grid */}
                  <div className="tw-grid tw-grid-cols-2 tw-gap-3 tw-mb-6">
                    <div className="tw-bg-gradient-to-br tw-from-blue-50 tw-to-blue-100 tw-p-3 tw-rounded-xl tw-border tw-border-blue-200">
                      <p className="tw-text-xs tw-text-blue-600 tw-font-medium tw-mb-1">
                        {t('Marketplace/detail/stats/price')}
                      </p>
                      <p className="tw-text-xl tw-font-bold tw-text-blue-900">${packageData.price}</p>
                    </div>
                    <div className="tw-bg-gradient-to-br tw-from-green-50 tw-to-green-100 tw-p-3 tw-rounded-xl tw-border tw-border-green-200">
                      <p className="tw-text-xs tw-text-green-600 tw-font-medium tw-mb-1">
                        {t('Marketplace/detail/stats/version')}
                      </p>
                      <p className="tw-text-xl tw-font-bold tw-text-green-900">v{packageData.version}</p>
                    </div>
                  </div>

                  <Link
                    href={`${process.env.NEXT_PUBLIC_DASHBOARD_BASE_URL as string}/${locale}/login`}
                    className="tw-w-full w-full tw-flex tw-justify-center tw-bg-gradient-to-r tw-from-blue-600 tw-to-indigo-600 tw-text-white tw-font-semibold tw-py-3 tw-px-6 tw-rounded-xl tw-shadow-lg hover:tw-from-blue-700 hover:tw-to-indigo-700 tw-transition-all tw-duration-200 hover:tw-shadow-xl tw-transform hover:tw-scale-105"
                  >
                    {t('Marketplace/detail/purchaseButton')}
                  </Link>
                </div>
              </div>
            </div>

            {/* Bots Configuration Section */}
            <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-p-8">
              <h2 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mb-6 tw-flex tw-items-center">
                <HiOutlineCpuChip className="tw-w-8 tw-h-8 tw-mr-3 tw-text-blue-600" />
                {t('Marketplace/detail/bots/title')}
              </h2>

              <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 lg:tw-grid-cols-3 tw-gap-6">
                {(() => {
                  const botCounts: { [key: string]: { count: number; originalType: string } } = {};
                  packageData.configuration.bots.forEach((bot) => {
                    const label = getBotTypeLabel(bot);
                    if (!botCounts[label]) {
                      botCounts[label] = { count: 0, originalType: bot };
                    }
                    botCounts[label].count += 1;
                  });

                  return Object.entries(botCounts).map(([type, { count, originalType }]) => {
                    const colors = getBotTypeColor(originalType);
                    return (
                      <div
                        key={type}
                        className={`tw-bg-gradient-to-br ${colors.gradient} tw-border ${colors.border} tw-rounded-xl tw-p-6`}
                      >
                        <div className="tw-flex tw-flex-col tw-items-center tw-text-center tw-space-y-4">
                          <div
                            className={`tw-flex tw-items-center tw-justify-center tw-w-16 tw-h-16 tw-bg-white tw-rounded-2xl tw-shadow-md ${colors.text}`}
                          >
                            {getBotTypeIcon(originalType)}
                          </div>
                          <div className="tw-flex-1">
                            <h3 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-2">{type}</h3>
                            <div
                              className={`tw-inline-flex tw-items-center tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-bold ${colors.badge} tw-shadow-sm`}
                            >
                              <HiOutlineTag className="tw-w-4 tw-h-4 tw-mr-2" />
                              {count} {count > 1 ? 'bots' : 'bot'}
                            </div>
                          </div>
                          <div className={`tw-w-full tw-h-1 tw-rounded-full ${colors.badge}`}></div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Right Column - Promotional Content */}
          {hasResources && (
            <div className="lg:tw-col-span-1">
              <div className="tw-bg-white tw-rounded-2xl tw-shadow-xl tw-overflow-hidden tw-sticky tw-top-8">
                <div className="tw-bg-gradient-to-br tw-from-gray-50 tw-to-white tw-p-6">
                  <h2 className="tw-text-xl tw-font-bold tw-text-gray-900 tw-mb-4 tw-flex tw-items-center">
                    <HiOutlineLink className="tw-w-6 tw-h-6 tw-mr-2 tw-text-blue-600" />
                    {t('Marketplace/detail/resources/title')}
                  </h2>
                  <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center">
                    <HiOutlineUser className="tw-w-4 tw-h-4 tw-mr-2" />
                    {t('Marketplace/detail/resources/creatorLabel')}
                  </h3>

                  <div className="tw-space-y-4">
                    {/* Promotional Video */}
                    {packageData.data.promotional_video && (
                      <div className="tw-bg-black tw-rounded-xl tw-overflow-hidden">
                        <video
                          src={packageData.data.promotional_video}
                          controls
                          className="tw-w-full tw-aspect-[9/16]"
                          poster={packageData.data.image}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    )}

                    {/* YouTube Link */}
                    {packageData.data.youtube_link && (
                      <a
                        href={packageData.data.youtube_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-flex tw-items-center tw-p-4 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-xl hover:tw-bg-red-100 tw-transition-all tw-duration-200 hover:tw-shadow-md tw-group"
                      >
                        <div className="tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-bg-red-600 tw-rounded-lg tw-flex-shrink-0">
                          <FaYoutube className="tw-w-6 tw-h-6 tw-text-white" />
                        </div>
                        <div className="tw-ml-4 tw-flex-1">
                          <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-1">
                            {t('Marketplace/detail/resources/youtube')}
                          </h3>
                        </div>
                        <HiOutlineChevronRight className="tw-w-5 tw-h-5 tw-text-red-600 tw-transform group-hover:tw-translate-x-1 tw-transition-transform" />
                      </a>
                    )}

                    {/* LinkedIn Link */}
                    {packageData.data.linkedin_link && (
                      <a
                        href={packageData.data.linkedin_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="tw-flex tw-items-center tw-p-4 tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-xl hover:tw-bg-blue-100 tw-transition-all tw-duration-200 hover:tw-shadow-md tw-group"
                      >
                        <div className="tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-bg-blue-700 tw-rounded-lg tw-flex-shrink-0">
                          <FaLinkedinIn className="tw-w-5 tw-h-5 tw-text-white" />
                        </div>
                        <div className="tw-ml-4 tw-flex-1">
                          <h3 className="tw-font-semibold tw-text-gray-900 tw-text-sm tw-mb-1">
                            {t('Marketplace/detail/resources/linkedin')}
                          </h3>
                        </div>
                        <HiOutlineChevronRight className="tw-w-5 tw-h-5 tw-text-blue-700 tw-transform group-hover:tw-translate-x-1 tw-transition-transform" />
                      </a>
                    )}

                    {/* Share Link */}
                    <div className="tw-pt-4 tw-border-t tw-border-gray-200">
                      <h3 className="tw-text-sm tw-font-semibold tw-text-gray-700 tw-mb-3 tw-flex tw-items-center">
                        <HiOutlineShare className="tw-w-4 tw-h-4 tw-mr-2" />
                        {t('Marketplace/detail/resources/shareTitle')}
                      </h3>
                      {copyFailed ? (
                        <div className="tw-space-y-2">
                          <p className="tw-text-xs tw-text-gray-500">
                            {t('Marketplace/detail/resources/selectAndCopy')}
                          </p>
                          <input
                            type="text"
                            readOnly
                            value={getShareableUrl()}
                            onClick={(e) => (e.target as HTMLInputElement).select()}
                            className="tw-w-full tw-p-3 tw-text-xs tw-bg-gray-100 tw-border tw-border-gray-300 tw-rounded-lg tw-text-gray-700 tw-select-all focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500"
                          />
                        </div>
                      ) : (
                        <button
                          onClick={copyShareableLink}
                          className={`tw-flex tw-items-center tw-w-full tw-p-4 tw-border tw-rounded-xl tw-transition-all tw-duration-200 hover:tw-shadow-md tw-group ${
                            copied
                              ? 'tw-bg-green-50 tw-border-green-300'
                              : 'tw-bg-blue-50 tw-border-blue-200 hover:tw-bg-blue-100'
                          }`}
                        >
                          <div
                            className={`tw-flex tw-items-center tw-justify-center tw-w-12 tw-h-12 tw-rounded-lg tw-flex-shrink-0 ${
                              copied ? 'tw-bg-green-600' : 'tw-bg-blue-600'
                            }`}
                          >
                            {copied ? (
                              <HiOutlineCheck className="tw-w-5 tw-h-5 tw-text-white" />
                            ) : (
                              <HiOutlineClipboardDocument className="tw-w-5 tw-h-5 tw-text-white" />
                            )}
                          </div>
                          <div className="tw-ml-4 tw-flex-1 tw-text-left">
                            <p className="tw-font-semibold tw-text-gray-900 tw-text-sm">
                              {copied
                                ? t('Marketplace/detail/resources/linkCopied')
                                : t('Marketplace/detail/resources/copyLink')}
                            </p>
                          </div>
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
